// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
// Error code constants
import { ErrorCode } from '@/api/types';

export const UNIQUE_VIOLATION_CODE = 'UNIQUE_VIOLATION';
export const CYCLICAL_REFERENCE_ERROR_CODE = 608;

// Complete error code mapping from Go backend constants
export const ERROR_CODE_MAPPING = {
  // 400 block errors
  bad_request: 400,
  entity_not_found: 404,

  // 600 block errors - general errors
  invalid_customer_id: 601,
  invalid_asset_id: 602,
  invalid_property_id: 603,
  invalid_query_parameters: 604,
  invalid_post_body: 605,
  invalid_segment_id: 606,
  invalid_asset_key: 607,
  cyclical_reference_not_allowed: 608,

  // 700 block errors - enumeration errors
  enum_invalid_integration_source: 700,
  enum_invalid_product_field_type: 701,

  // 800 block errors - general errors
  general_invalid_upload: 800,
  general_invalid_header_row: 801,

  // 900 block errors - custom server-side errors
  sample_rate: 900,

  // 1000 block errors - SSO related errors
  oauth_must_be_google_managed_org: 1000,
  oauth_gmail_not_allowed: 1001,
  oauth_email_must_be_verified: 1002,
  oauth_domain_not_verified: 1003,
  oauth_custom_domain_required: 1004,
  oauth_email_not_found: 1005,
  oauth_identity_mismatch: 1006,

  // 1100 block errors - asset errors
  asset_invalid_transit_status: 1100,
  asset_invalid_inventory_status: 1101,
  asset_invalid_company_status: 1102,
  asset_invalid_project_status: 1103,
  asset_invalid_ownership_date: 1104,
  asset_valid_origin_or_destination_required: 1105,
  asset_disposal_date_required: 1106,
  asset_invalid_metadata: 1107,
  asset_origin_required_before_ship_date: 1108,
  asset_destination_required_before_deliver_date: 1109,
  asset_occurred_at_times_out_of_order: 1110,
  asset_already_scrapped: 1111,
  asset_must_be_unassigned: 1112,
  asset_not_scrapped: 1113,
  asset_scrapped_mutation: 1114,
  asset_scrapped_no_disposal_for_sold_asset: 1115,
  asset_mismatched_metadata_type: 1116,
  asset_serial_not_found: 1117,
  asset_mismatched_product_metadata: 1118,
  asset_invalid_status: 1119,
  asset_invalid_serial: 1120,
  asset_invalid_inventory_date: 1121,
  asset_invalid_initial_location: 1122,
  asset_invalid_owner: 1123,
  asset_invalid_description: 1124,
  asset_invalid_field: 1125,
  asset_duplicate_ownership_date: 1127,
  asset_sale_date_in_future: 1134,
  asset_already_assigned_to_project: 1135,
  asset_disposal_date_in_future: 1136,
  asset_move_destination_customer_mismatch: 1137,
  asset_move_destination_required: 1138,
  asset_move_delivery_date_required: 1139,
  asset_move_delivery_date_in_future: 1140,
  asset_resolve_serial_limit_exceeded: 1141,
  asset_invalid_cost_adjustment_type: 1142,
  asset_invalid_cost_adjustment_reason: 1143,
  asset_invalid_cost_adjustment_amount: 1144,
  asset_cost_adjustment_date_in_future: 1145,
  asset_cost_adjustment_notes_required: 1146,
  asset_cost_adjustment_date_required: 1147,
  asset_cost_adjustment_exceeds_book_value: 1148,
  asset_invalid_useful_life_revision_reason: 1149,
  asset_useful_life_revision_date_in_future: 1150,
  asset_useful_life_revision_notes_required: 1151,
  asset_useful_life_revision_date_required: 1152,
  asset_move_conflict: 1153,
  asset_mixed_currency: 1154,
  asset_move_limit_exceeded: 1160,
  asset_move_ship_date_in_future: 1166,
  asset_event_type_requires_event_group: 2920,
  asset_moves_required: 1167,
  asset_move_id_required: 1168,
  asset_move_time_required: 1169,
  asset_move_invalid_time: 1170,
  asset_move_invalid_timezone: 1171,

  // 1200 block errors - customer errors
  customer_name_required: 1200,
  customer_invalid_target_customer_id: 1201,
  customer_billing_address_required: 1202,
  customer_billing_contact_required: 1203,
  customer_comment_required: 1204,
  customer_invalid_key: 1205,
  customer_merge_removed_not_found: 1215,

  // 1300 block errors - notification errors
  notification_invalid_topic: 1300,
  notification_invalid_channel: 1301,

  // 1400 block errors - organization errors
  organization_invalid_name: 1400,
  organization_invalid_timezone: 1401,
  organization_cannot_modify_self: 1402,
  organization_external_user_invalid_name: 1403,
  organization_external_user_invalid_label: 1404,
  organization_external_user_invalid_email: 1405,
  organization_external_user_duplicate_email: 1406,
  organization_deactivated: 1409,
  organization_access_ended: 1410,
  organization_member_deactivated: 1411,

  // 1500 block errors - product errors
  product_invalid_name: 1500,
  product_archived_must_be_set: 1501,
  product_field_must_exist: 1502,
  product_field_label_required: 1503,
  product_field_type_required: 1504,
  product_field_section_required: 1505,
  product_field_order_required: 1506,
  product_invalid_id: 1507,
  product_sku_exists: 1508,
  product_field_invalid_number: 1509,
  product_field_invalid_date: 1510,
  product_field_invalid_boolean: 1511,
  product_field_invalid_text: 1512,
  product_field_invalid_integer: 1513,
  product_field_invalid_datetime: 1514,
  product_field_invalid_time: 1515,
  product_field_invalid_type: 1516,
  product_field_invalid_upload: 1517,
  product_field_invalid_serial_header: 1518,
  product_field_invalid_header: 1519,
  product_not_found: 1520,
  product_has_assets: 1521,
  product_key_required: 1522,
  product_merge_same_product: 1523,
  product_merge_field_conflict: 1524,
  product_merge_inventory_conflict: 1525,
  product_merge_crm_conflict: 1526,
  item_merge_bulk_not_allowed: 1539,
  item_invalid_type: 1537,
  bom_only_device_items: 1542,
  item_has_inventory_movements: 1543,
  marked_bulk_to_unmarked_not_allowed: 1544,

  // 1600 block errors - project related errors
  project_assignment_start_date_required: 1600,
  project_assignment_effective_date_must_be_set: 1601,
  project_name_required: 1602,
  project_product_id_required: 1603,
  project_start_time_required: 1604,
  project_quantity_must_be_positive: 1605,
  project_cost_amount_required: 1606,
  project_cost_description_required: 1607,
  project_amount_or_description_required: 1608,
  project_cannot_be_cyclical: 1609,
  project_cost_incurred_on_required: 1610,
  project_workflow_missing: 1614,
  project_workflow_stage_missing: 1615,
  project_must_have_no_costs: 1616,
  invalid_project_id: 1617,
  project_has_invoiced_charges: 1635,
  project_ends_before_it_starts: 1644,
  project_text_too_long: 1645,
  project_line_item_quantity_too_large: 1647,
  project_line_item_sent_more_than_once: 1656,
  project_line_item_override_below_invoiced: 1658,

  // 1700 block errors - reporting related errors
  reporting_invalid_start: 1700,
  reporting_invalid_end: 1701,
  reporting_invalid_range: 1702,
  reporting_range_too_large: 1703,
  reporting_missing_as_of_date: 1704,
  reporting_invalid_as_of_date: 1705,

  // 1800 block errors - location related errors
  location_invalid: 1800,
  location_not_found: 1801,
  location_merge_invalid: 1802,
  location_invalid_name: 1803,
  location_invalid_latitude: 1804,
  location_invalid_longitude: 1805,
  location_invalid_address: 1806,
  location_invalid_is_inventory: 1807,
  location_invalid_is_transient: 1808,
  location_invalid_company: 1809,
  location_duplicate_name: 1810,
  location_missing_address: 1811,
  zone_name_matches_parent: 1813,
  site_name_matches_zone: 1814,
  location_merge_into_own_zone: 1815,
  location_field_not_valid_for_type: 1816,
  location_type_required: 1817,
  zone_parent_location_required: 1818,
  location_type_cannot_change: 2935,

  // 1900 block errors - note related errors
  note_content_required: 1900,
  note_key_required: 1901,

  // 2000 block errors - ticket related errors
  ticket_not_found: 2000,
  ticket_invalid: 2001,
  ticket_invalid_status: 2002,
  ticket_invalid_type: 2003,
  ticket_invalid_priority: 2004,
  ticket_invalid_title: 2005,
  ticket_invalid_description: 2006,
  ticket_invalid_due_date: 2007,
  ticket_invalid_assigned_to: 2008,
  ticket_invalid_completed_by: 2009,
  ticket_invalid_completed_on: 2010,

  // 2100 block errors - parts related errors
  part_not_found: 2100,
  part_invalid_name: 2101,
  part_invalid_sku: 2102,
  part_invalid_unit_of_measure: 2103,
  part_invalid_quantity: 2104,
  invalid_part_origin: 2105,
  invalid_part_destination: 2106,
  invalid_part: 2107,
  inventory_removal_exceeds_stock: 2116,

  // 2200 block errors - inventory/shipment related errors
  invalid_inventory_item: 2200,
  invalid_inventory_item_type: 2201,
  invalid_shipment_to: 2202,
  invalid_shipment_quantity: 2203,
  invalid_shipment_received_at: 2204,
  invalid_shipment_delivered_at: 2205,
  shipment_already_delivered: 2206,
  shipment_already_received: 2207,
  shipment_must_be_delivered: 2208,
  invalid_shipment_status: 2209,
  invalid_delivery_location: 2210,
  invalid_shipment_location: 2214,
  invalid_shipment_ship_location: 2219,
  invalid_fulfillment_order_number: 2224,
  invalid_purchase_order_number: 2257,
  invalid_return_order_number: 2258,
  fulfillment_order_cannot_close: 2263,
  organization_third_party_fulfillment_not_enabled: 2240,
  location_not_third_party_enabled: 2241,
  location_missing_third_party_identifier: 2242,
  third_party_provider_not_found: 2243,
  third_party_provider_unreachable: 2246,
  third_party_logistics_disabled: 2250,
  third_party_provider_rejected: 2267,
  shipment_cancelled: 2268,
  shipment_locked_by_3pl: 2269,
  shipment_location_locked: 2270,
  shipment_order_finished: 2272,
  shipment_order_locked: 2273,
  shipment_received_at_in_future: 2294,
  shipment_shipped_at_in_future: 2296,
  shipment_delivered_at_in_future: 2298,
  shipment_order_direction_mismatch: 2930,
  organization_invalid_serial_pattern: 2271,
  quarantine_serial_exists: 5026,
  quarantine_serial_held: 5027,
  membership_rules_invalid: 5030,
  membership_project_has_no_window: 5032,

  // 6000 block errors - plan limits
  plan_limit_exceeded: 6000,

  // 2300 block errors - assembly related errors
  assembly_invalid_assembly: 2300,
  assembly_invalid_part_id: 2301,
  assembly_invalid_quantity: 2302,
  assembly_invalid_build_date: 2303,
  assembly_invalid_location_id: 2304,
  assembly_invalid_product_id: 2305,
  assembly_invalid_asset_serial: 2306,

  // 2400 block errors - file and URL link related errors
  url_link_key_required: 2400,
  url_link_url_required: 2401,
  url_link_url_or_name_required: 2402,
  url_link_url_cannot_be_empty: 2403,

  // 2500 block errors - project workflow errors
  project_workflow_invalid_workflow_name: 2500,
  project_workflow_invalid_workflow_stage_name: 2501,
  project_workflow_invalid_order_index: 2502,
  project_workflow_invalid_stage_count: 2503,
  project_workflow_duplicate_stage_id: 2504,
  project_workflow_invalid_stage_id: 2505,
  project_workflow_invalid_item_id: 2506,
  project_workflow_cannot_delete_last_stage: 2507,
  project_workflow_duplicate_item_ordering: 2508,
  project_workflow_invalid_deletion_strategy: 2509,
  project_workflow_invalid_migrate_to: 2510,
  project_workflow_cannot_delete_last_workflow: 2511,
  project_workflow_cannot_delete_in_use_workflow: 2512,

  // 2600 block errors - person errors
  invalid_person_id: 2600,

  // 2700 block errors - cost errors
  invalid_cost_id: 2700,
  cost_incurred_on_required: 2701,
  cost_description_required: 2702,
  cost_amount_required: 2703,
  cost_invalid_amount: 2704,
  cost_invalid_person: 2705,
  cost_invalid_project: 2706,
  cost_invalid_date: 2707,
  cost_invalid_import: 2708,

  // 2800 block errors - accounting errors
  accounting_invalid_salvage_value: 2801,
  accounting_invalid_useful_life: 2802,
  accounting_invalid_in_service_date: 2803,
  accounting_invalid_simple_cost_basis: 2804,
  accounting_invalid_bill_of_materials: 2805,
  accounting_invalid_direct_labor: 2806,
  accounting_invalid_allocated_indirect: 2807,
  accounting_invalid_freight_inbound: 2808,
  accounting_invalid_freight_outbound: 2809,
  accounting_invalid_installation: 2810,
  accounting_invalid_tax: 2811,
  accounting_invalid_tariffs: 2812,
  accounting_invalid_interest: 2813,
  accounting_invalid_request: 2814,
  accounting_period_already_locked: 2815,
  accounting_initial_true_up_already_exists: 2822,
  accounting_period_not_sequential: 2824,
  accounting_period_not_completed: 2825,
  accounting_mixed_currency: 2826,
  accounting_initial_true_up_required: 2827,
  accounting_initial_true_up_period_not_latest_locked_period: 2828,
  accounting_initial_true_up_currency_has_no_assets: 2829,
  accounting_close_run_missing_currency: 2830,

  // 3400 block errors - contact errors
  contact_not_found: 3404,
  contact_merge_removed_not_found: 3417,

  // 3600 block errors - invoice errors
  invoice_already_open: 3600,
  invoice_already_draft: 3601,
  invoice_no_line_items: 3602,
  invoice_has_settlements: 3603,
  invoice_customer_not_found: 3604,
  invoice_already_void: 3605,
  invoice_not_voidable: 3606,
  invoice_void_reason_required: 3607,
  invoice_not_deletable: 3608,
  invoice_not_payable: 3609,
  payment_exceeds_balance: 3610,
  invoice_missing_customer: 3611,
  invoice_missing_due_date: 3612,
  invoice_number_duplicate: 3613,
  invoice_not_sendable: 3614,
  invoice_sent_at_future: 3615,
  charge_not_cancellable: 3700,
  charge_not_markable_billable: 3701,
  charge_not_markable_not_billable: 3702,
  charge_not_revertable: 3703,
  billing_plan_not_periodic: 3713,
  billing_plan_not_pending: 3714,
  billing_plan_has_locked_charges: 3715,
  billing_plan_missing_schedule_config: 3716,
  billing_plan_cascade_count_mismatch: 3717,
  billing_plan_invalid_service_period: 3718,
  charge_not_attachable_to_invoice: 3722,
  billing_plan_activation_date_out_of_range: 3723,
  charge_quantity_too_large: 3729,
  charge_label_required: 3730,
  charge_label_too_long: 3731,
  charge_sequence_cannot_be_negative: 3732,
  charge_sequence_sent_more_than_once: 3733,
  charge_sent_more_than_once: 3734,
  charge_billing_eligible_date_required: 3735,
  charge_mark_billable_quantity_required: 3736,
  charge_trigger_type_event_not_accepted: 2945,
  charge_already_attached_to_invoice: 5021,
  sfdc_location_not_linked: 2904,
  sfdc_drift_field_not_monitored: 2905,
  sfdc_drift_field_not_actionable: 2906,
  sfdc_drift_field_pull_unsupported: 2908,
  sfdc_drift_field_name_required: 2909,
  sfdc_reference_account_cache_pending: 2911,
  sfdc_reference_account_cache_corrupt: 2912,

  // 3800 block errors - currency value validation (Currency type)
  currency_value_exceeds_maximum: 3800,
  currency_unknown_iso: 3801,
  currency_mixed_iso: 3802,
  currency_division_by_zero: 3803,
  currency_sub_cent_precision: 3804,
  currency_missing_iso: 3805,
  currency_invalid_payload: 3806,
  currency_invalid_amount: 3807,
  currency_zero_decimal_precision: 3808,
  currency_not_supported: 3809,
  project_mixed_currency: 3810,
  invoice_mixed_currency: 3811,
  asset_transaction_currency_mismatch: 3812,
  currency_negative_amount: 3815,

  // 4100 block errors - hubspot integration errors
  hubspot_not_configured: 4100,
  hubspot_invalid_pipeline: 4101,
  hubspot_invalid_pipeline_stage: 4102,

  // 4200 block errors - company request validation errors
  customer_invalid_billing_contact_email: 4200,
} as const;

// Error code mapping
export const ERROR_CODES: Record<number, ErrorCode> = {
  608: 'cyclical_reference',
  800: 'invalid_csv_format',
  1000: 'oauth_must_be_google_managed_org',
  1001: 'oauth_gmail_not_allowed',
  1002: 'oauth_email_must_be_verified',
  1003: 'oauth_domain_not_verified',
  1004: 'oauth_custom_domain_required',
  1005: 'oauth_email_not_found',
  1006: 'oauth_identity_mismatch',
  1402: 'user_cannot_modify_self',
  1409: 'organization_deactivated',
  1410: 'organization_access_ended',
  1411: 'organization_member_deactivated',
  1500: 'product_invalid_name',
  1501: 'product_archived_must_be_set',
  1502: 'product_field_must_exist',
  1503: 'product_field_label_required',
  1504: 'product_field_type_required',
  1505: 'product_field_section_required',
  1506: 'product_field_order_required',
  1507: 'product_invalid_id',
  1508: 'sku_exists',
  1509: 'product_field_invalid_number',
  1510: 'product_field_invalid_date',
  1511: 'product_field_invalid_boolean',
  1512: 'product_field_invalid_text',
  1513: 'product_field_invalid_integer',
  1514: 'product_field_invalid_datetime',
  1515: 'product_field_invalid_time',
  1516: 'product_field_invalid_type',
  1517: 'product_field_invalid_upload',
  1518: 'product_field_invalid_serial_header',
  1519: 'product_field_invalid_header',
  1520: 'product_not_found',
  1521: 'product_has_assets',
  1522: 'product_key_required',
  1523: 'product_merge_same_product',
  1524: 'product_merge_field_conflict',
  1525: 'product_merge_inventory_conflict',
  1526: 'product_merge_crm_conflict',
  1537: 'item_invalid_type',
  1539: 'item_merge_bulk_not_allowed',
  1542: 'bom_only_device_items',
  1543: 'item_has_inventory_movements',
  1544: 'marked_bulk_to_unmarked_not_allowed',
  1602: 'project_name_required',
  1616: 'project_has_costs',
  1635: 'project_has_invoiced_charges',
  1644: 'project_ends_before_it_starts',
  1658: 'project_line_item_override_below_invoiced',
  1703: 'date_range_exceeded',
  1801: 'location_not_found',
  1810: 'location_duplicate_name',
  1811: 'location_missing_address',
  1813: 'zone_name_matches_parent',
  1814: 'site_name_matches_zone',
  1815: 'location_merge_into_own_zone',
  1816: 'location_field_not_valid_for_type',
  2935: 'location_type_cannot_change',
  2105: 'invalid_part_origin',
  2106: 'invalid_part_destination',
  2116: 'inventory_removal_exceeds_stock',
  2210: 'invalid_delivery_location',
  2214: 'invalid_shipment_location',
  2219: 'invalid_shipment_ship_location',
  2240: 'organization_third_party_fulfillment_not_enabled',
  2241: 'location_not_third_party_enabled',
  2242: 'location_missing_third_party_identifier',
  2243: 'third_party_provider_not_found',
  2246: 'third_party_provider_unreachable',
  2250: 'third_party_logistics_disabled',
  2267: 'third_party_provider_rejected',
  2268: 'shipment_cancelled',
  2269: 'shipment_locked_by_3pl',
  2270: 'shipment_location_locked',
  2271: 'organization_invalid_serial_pattern',
  2272: 'shipment_order_finished',
  2273: 'shipment_order_locked',
  2294: 'shipment_received_at_in_future',
  2296: 'shipment_shipped_at_in_future',
  2298: 'shipment_delivered_at_in_future',
  2930: 'shipment_order_direction_mismatch',
  5026: 'quarantine_serial_exists',
  5027: 'quarantine_serial_held',
  5030: 'membership_rules_invalid',
  5032: 'membership_project_has_no_window',
  6000: 'plan_limit_exceeded',
  2511: 'project_workflow_cannot_delete_last_workflow',
  2512: 'project_workflow_cannot_delete_in_use_workflow',
  1137: 'asset_move_destination_customer_mismatch',
  1138: 'asset_move_destination_required',
  1139: 'asset_move_delivery_date_required',
  1140: 'asset_move_delivery_date_in_future',
  1141: 'asset_resolve_serial_limit_exceeded',
  1142: 'asset_invalid_cost_adjustment_type',
  1143: 'asset_invalid_cost_adjustment_reason',
  1144: 'asset_invalid_cost_adjustment_amount',
  1145: 'asset_cost_adjustment_date_in_future',
  1146: 'asset_cost_adjustment_notes_required',
  1147: 'asset_cost_adjustment_date_required',
  1148: 'asset_cost_adjustment_exceeds_book_value',
  1149: 'asset_invalid_useful_life_revision_reason',
  1150: 'asset_useful_life_revision_date_in_future',
  1151: 'asset_useful_life_revision_notes_required',
  1152: 'asset_useful_life_revision_date_required',
  1153: 'asset_move_conflict',
  1154: 'asset_mixed_currency',
  1160: 'asset_move_limit_exceeded',
  1166: 'asset_move_ship_date_in_future',
  2920: 'asset_event_type_requires_event_group',
  3600: 'invoice_already_open',
  3601: 'invoice_already_draft',
  3603: 'invoice_has_settlements',
  3604: 'invoice_customer_not_found',
  3605: 'invoice_already_void',
  3606: 'invoice_not_voidable',
  3607: 'invoice_void_reason_required',
  3608: 'invoice_not_deletable',
  3609: 'invoice_not_payable',
  3610: 'payment_exceeds_balance',
  3611: 'invoice_missing_customer',
  3612: 'invoice_missing_due_date',
  3613: 'invoice_number_duplicate',
  3614: 'invoice_not_sendable',
  3615: 'invoice_sent_at_future',
  3700: 'charge_not_cancellable',
  3701: 'charge_not_markable_billable',
  3702: 'charge_not_markable_not_billable',
  3703: 'charge_not_revertable',
  3713: 'billing_plan_not_periodic',
  3714: 'billing_plan_not_pending',
  3715: 'billing_plan_has_locked_charges',
  3716: 'billing_plan_missing_schedule_config',
  3717: 'billing_plan_cascade_count_mismatch',
  3718: 'billing_plan_invalid_service_period',
  3722: 'charge_not_attachable_to_invoice',
  3723: 'billing_plan_activation_date_out_of_range',
  2945: 'charge_trigger_type_event_not_accepted',
  5021: 'charge_already_attached_to_invoice',
  2802: 'accounting_invalid_useful_life',
  2822: 'accounting_initial_true_up_already_exists',
  2826: 'accounting_mixed_currency',
  2827: 'accounting_initial_true_up_required',
  2828: 'accounting_initial_true_up_period_not_latest_locked_period',
  2829: 'accounting_initial_true_up_currency_has_no_assets',
  2830: 'accounting_close_run_missing_currency',
  2904: 'sfdc_location_not_linked',
  2905: 'sfdc_drift_field_not_monitored',
  2906: 'sfdc_drift_field_not_actionable',
  2908: 'sfdc_drift_field_pull_unsupported',
  2909: 'sfdc_drift_field_name_required',
  2911: 'sfdc_reference_account_cache_pending',
  2912: 'sfdc_reference_account_cache_corrupt',
  3800: 'currency_value_exceeds_maximum',
  3801: 'currency_unknown_iso',
  3802: 'currency_mixed_iso',
  3803: 'currency_division_by_zero',
  3804: 'currency_sub_cent_precision',
  3805: 'currency_missing_iso',
  3806: 'currency_invalid_payload',
  3807: 'currency_invalid_amount',
  3808: 'currency_zero_decimal_precision',
  3809: 'currency_not_supported',
  3810: 'project_mixed_currency',
  3811: 'invoice_mixed_currency',
  3812: 'asset_transaction_currency_mismatch',
  3815: 'currency_negative_amount',
  4100: 'hubspot_not_configured',
  4101: 'hubspot_invalid_pipeline',
  4102: 'hubspot_invalid_pipeline_stage',
  4200: 'customer_invalid_billing_contact_email',
};

// Error messages mapping
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  date_range_exceeded: 'Date range exceeded the allowed limit.',
  invalid_csv_format:
    'The uploaded CSV file is malformed or has invalid formatting.',
  sku_exists: 'A product with this SKU already exists.',
  user_cannot_modify_self: 'You cannot modify your own account.',
  product_invalid_name: 'Invalid product name provided.',
  product_archived_must_be_set: 'Product archive status must be specified.',
  product_field_must_exist: 'Product field must have a valid identifier.',
  product_field_label_required: 'Product field label is required.',
  product_field_type_required: 'Product field type is required.',
  product_field_section_required: 'Product field section is required.',
  product_field_order_required: 'Product field order is required.',
  product_invalid_id: 'Invalid product identifier provided.',
  product_field_invalid_number: 'Invalid number format in product field.',
  product_field_invalid_date: 'Invalid date format in product field.',
  product_field_invalid_boolean: 'Invalid boolean value in product field.',
  product_field_invalid_text: 'Invalid text value in product field.',
  product_field_invalid_integer: 'Invalid integer value in product field.',
  product_field_invalid_datetime: 'Invalid date/time format in product field.',
  product_field_invalid_time: 'Invalid time format in product field.',
  product_field_invalid_type: 'Invalid product field type.',
  product_field_invalid_upload: 'Invalid file upload in product field.',
  product_field_invalid_serial_header:
    'Invalid serial number header in product field.',
  product_field_invalid_header: 'Invalid header in product field.',
  product_not_found: 'Product not found.',
  product_has_assets:
    'Cannot change product type because it has existing assets.',
  product_key_required: 'Product key is required.',
  product_merge_same_product: 'Cannot merge a product into itself.',
  product_merge_field_conflict:
    'Products have fields with the same name but different types.',
  product_merge_inventory_conflict:
    'Non-inventory products can only be merged with other non-inventory products.',
  product_merge_crm_conflict:
    'Both products are linked to CRM systems. Please merge them in your CRM first and then merge in Hardfin.',
  item_merge_bulk_not_allowed:
    'Only services and tracked devices can be merged.',
  item_invalid_type: 'Bulk items cannot be converted to tracked devices.',
  bom_only_device_items: 'Bill of materials can only be added to device items.',
  item_has_inventory_movements:
    'Cannot change type: item has inventory movements.',
  marked_bulk_to_unmarked_not_allowed:
    'Marked bulk items cannot be converted to unmarked bulk.',
  cyclical_reference:
    'A cyclical reference was detected. Please fix the reference and try again. If the problem persists, please contact support.',
  project_ends_before_it_starts: 'A project cannot end before it starts.',
  project_name_required: 'A project name is required.',
  project_has_costs: 'Cannot delete project because it has associated costs.',
  project_has_invoiced_charges:
    'Cannot complete this action because of invoiced charges',
  project_line_item_override_below_invoiced:
    'Total override cannot be less than the invoiced charges',
  project_workflow_cannot_delete_last_workflow:
    'At least one project workflow must remain. Add another workflow before deleting this one.',
  project_workflow_cannot_delete_in_use_workflow:
    'This workflow cannot be deleted because one or more projects are currently using it. Update those projects to a different workflow or disable their workflow before deleting this workflow.',
  organization_third_party_fulfillment_not_enabled:
    'This organization does not have third-party fulfillment enabled. Please contact your account rep to enable this feature.',
  organization_deactivated:
    'This account has been deactivated, please contact support@hardfin.com.',
  organization_access_ended:
    "This organization's access has ended, please contact support@hardfin.com.",
  organization_member_deactivated:
    'User account has been deactivated, please contact your admin.',
  oauth_must_be_google_managed_org:
    "Sign in with your organization's Google Workspace account.",
  oauth_gmail_not_allowed:
    "Personal Gmail accounts can't sign in. Use your organization's Google Workspace account.",
  oauth_email_must_be_verified:
    'Verify your email address with Google, then sign in again.',
  oauth_domain_not_verified:
    'Your email domain is not verified for Microsoft sign-in, please contact support@hardfin.com.',
  oauth_custom_domain_required:
    'Your Microsoft organization needs a verified custom domain before it can sign up. Ask your admin to add one, then sign in again.',
  oauth_email_not_found:
    'Your Microsoft account has no email address Hardfin can use, please contact your admin.',
  oauth_identity_mismatch:
    'This email is already linked to a different sign-in account, please contact support@hardfin.com.',
  location_not_third_party_enabled:
    'The origin location must be configured for third-party fulfillment. Please update the location settings.',
  location_missing_third_party_identifier:
    'The origin location is missing a third-party provider identifier. Please update the location configuration.',
  third_party_provider_not_found:
    'The third-party logistics provider could not be found. Please contact support.',
  third_party_provider_unreachable:
    "We can't reach your logistics provider, please try again later.",
  third_party_logistics_disabled:
    'Third-party logistics is not available in this environment.',
  third_party_provider_rejected:
    'Your 3PL rejected this request. Hardfin support has been notified to investigate.',
  shipment_cancelled:
    'This shipment has been cancelled, so it can no longer be changed.',
  shipment_locked_by_3pl:
    'Your third-party logistics provider manages this shipment. Make this change with the provider instead.',
  shipment_location_locked:
    'This shipment has already moved inventory, so its locations can no longer be changed.',
  shipment_order_finished:
    'That order is cancelled, closed, or already complete, so it takes no further shipments.',
  shipment_order_locked:
    'This shipment is no longer open, so the order it belongs to can no longer be changed.',
  shipment_received_at_in_future: 'Receive date cannot be in the future.',
  shipment_shipped_at_in_future: 'Ship date cannot be in the future.',
  shipment_delivered_at_in_future: 'Delivery date cannot be in the future.',
  inventory_removal_exceeds_stock:
    'There is insufficient quantity at this location.',
  shipment_order_direction_mismatch:
    'An outbound shipment can only belong to a fulfillment order, and an inbound shipment only to a purchase or return order.',
  organization_invalid_serial_pattern:
    'One of these serial patterns cannot be saved. Check that every row has the length or prefix it needs, and that no two rows are the same.',
  quarantine_serial_exists:
    'This serial already exists in Hardfin, so a new asset cannot be created for it. Use a different serial.',
  quarantine_serial_held:
    'This serial is already on this shipment as a different device, so it cannot be used again',
  membership_rules_invalid:
    'These membership rules cannot be saved. Every rule needs at least one SKU and one way for an asset to join.',
  membership_project_has_no_window:
    'This project needs a start date before its membership can be charted',
  plan_limit_exceeded:
    'Trial limits exceeded, contact sales@hardfin.com to upgrade your account',
  location_duplicate_name:
    'A zone with this name already exists at this location',
  zone_name_matches_parent:
    'Zone name cannot be the same as the parent site name',
  site_name_matches_zone:
    'Location name cannot be the same as one of its zone names',
  location_merge_into_own_zone: "Zones can't be merged with their own site",
  location_field_not_valid_for_type:
    'That field does not apply to this kind of record',
  location_type_cannot_change: "A location's type can't be changed",
  location_missing_address:
    'The destination location is missing address data required for shipping.',
  location_not_found: 'A selected location is missing or no longer exists.',
  invalid_part_origin:
    'The origin location is missing, no longer exists, or is the same as the destination.',
  invalid_part_destination:
    'The destination location is missing or no longer exists.',
  invalid_delivery_location:
    'The delivery location is missing or no longer exists.',
  invalid_shipment_location: "The shipment's origin location no longer exists.",
  invalid_shipment_ship_location:
    "The ship-from location is missing, no longer exists, or does not match the shipment's origin.",
  asset_move_destination_customer_mismatch:
    'current location customer does not match existing customer for current location name',
  asset_move_destination_required: 'current location name is required',
  asset_move_delivery_date_required: 'location arrival date is required',
  asset_move_delivery_date_in_future:
    'location arrival date cannot be in the future',
  asset_resolve_serial_limit_exceeded:
    'Too many serials were submitted to resolve inline. Search for the serial from the asset list instead.',
  asset_invalid_cost_adjustment_type: 'Invalid cost adjustment type.',
  asset_invalid_cost_adjustment_reason:
    'Reason is invalid for this adjustment type.',
  asset_invalid_cost_adjustment_amount: 'Amount must be greater than zero.',
  asset_cost_adjustment_date_in_future:
    'Effective date cannot be in the future.',
  asset_cost_adjustment_notes_required:
    'Notes are required when reason is Other.',
  asset_cost_adjustment_date_required: 'Effective date is required.',
  asset_cost_adjustment_exceeds_book_value:
    "Impairment amount cannot exceed the asset's book value on the effective date.",
  asset_invalid_useful_life_revision_reason:
    'Invalid useful life revision reason.',
  asset_useful_life_revision_date_in_future:
    'Effective date cannot be in the future.',
  asset_useful_life_revision_notes_required:
    'Notes are required when reason is Other.',
  asset_useful_life_revision_date_required: 'Effective date is required.',
  asset_move_conflict:
    'This conflicts with a movement already recorded for the asset. Correct the dates on the conflicting move before continuing.',
  asset_mixed_currency:
    'All cost fields must share one currency, and an asset with a locked cost currency cannot be re-denominated.',
  asset_move_limit_exceeded:
    'Too many moves were submitted at once. Record 500 or fewer moves at a time.',
  asset_move_ship_date_in_future: 'Ship date cannot be in the future.',
  asset_event_type_requires_event_group:
    'This event can only be recorded as part of an event group, such as a shipment.',
  accounting_invalid_useful_life:
    'Useful life must be between 1 and 480 months.',
  accounting_initial_true_up_already_exists:
    'This currency already has a locked initial true-up.',
  accounting_initial_true_up_required:
    'Every cost currency needs an initial true-up before the period can be closed.',
  accounting_initial_true_up_period_not_latest_locked_period:
    "An initial true-up must use the most recently closed period, or the first initial true-up's period if no period has been closed yet.",
  accounting_close_run_missing_currency:
    'A currency was trued up after this close was prepared. Refresh the page and start the close again to include it.',
  accounting_mixed_currency:
    'All accounting amounts must use the same currency.',
  accounting_initial_true_up_currency_has_no_assets:
    'Your organization has no assets in this currency, so it cannot be trued up.',
  invoice_already_open: 'This invoice is already open.',
  invoice_already_draft: 'This invoice is already a draft.',
  invoice_has_settlements:
    'This invoice has recorded payments and cannot be voided.',
  invoice_customer_not_found: 'The selected customer could not be found.',
  invoice_already_void: 'This invoice has already been voided.',
  invoice_not_voidable: "This invoice can't be voided in its current state.",
  invoice_void_reason_required: 'A reason is required to void this invoice.',
  invoice_not_deletable: "This invoice can't be deleted in its current state.",
  invoice_not_payable:
    'Payments can only be recorded on open or partially paid invoices.',
  payment_exceeds_balance: 'Payment amount exceeds the outstanding balance.',
  invoice_missing_customer:
    'A customer must be assigned before the invoice can be opened.',
  invoice_missing_due_date:
    'A due date must be set before the invoice can be opened.',
  invoice_number_duplicate: 'An invoice with this number already exists.',
  invoice_not_sendable:
    'Only open, partially paid, or paid invoices can be marked as sent.',
  invoice_sent_at_future: 'Sent-at time cannot be in the future.',
  charge_not_cancellable:
    "This charge can't be cancelled in its current state.",
  charge_not_markable_billable:
    'Only planned charges can be marked as billable.',
  charge_not_markable_not_billable:
    "This charge can't be marked as not billable in its current state.",
  charge_not_revertable:
    'Only cancelled charges can have their cancellation reverted.',
  billing_plan_not_periodic:
    'Only periodic billing plans can be confirmed from this dialog.',
  billing_plan_not_pending:
    'This billing plan has already been confirmed. Edit activation date from the plan drawer.',
  billing_plan_has_locked_charges:
    'This plan has charges that are already billable or invoiced. Edit it from the plan drawer.',
  billing_plan_missing_schedule_config:
    'Billing schedule is incomplete. Set the charge frequency, invoice frequency, and invoice mode before confirming.',
  billing_plan_cascade_count_mismatch:
    'The plan changed while you were editing. Reload and try again.',
  billing_plan_invalid_service_period:
    'Service period end date cannot be before the start date.',
  charge_not_attachable_to_invoice:
    'This charge can no longer be attached to the invoice. Pick another charge.',
  billing_plan_activation_date_out_of_range:
    'Activation date must be between January 1, 1970 and December 31, 2099',
  charge_trigger_type_event_not_accepted:
    'A charge must be triggered by time or manually.',
  charge_already_attached_to_invoice:
    'This charge is already attached to an invoice. Pick another charge.',
  sfdc_location_not_linked: 'This location is not linked to Salesforce.',
  sfdc_drift_field_not_monitored:
    "This field isn't tracked for Salesforce sync.",
  sfdc_drift_field_not_actionable:
    'This field cannot be pulled from Salesforce.',
  sfdc_drift_field_pull_unsupported:
    'Pulling this field from Salesforce is not yet supported.',
  sfdc_drift_field_name_required: 'Field name is required.',
  sfdc_reference_account_cache_pending:
    "Salesforce hasn't finished syncing the referenced account yet. Try again in a few minutes.",
  sfdc_reference_account_cache_corrupt:
    'The referenced Salesforce account is missing required data. Fix the record in Salesforce, then retry.',
  currency_value_exceeds_maximum: 'Amount must not exceed $999,999,999,999.99.',
  currency_unknown_iso: 'Unknown or unsupported currency code.',
  currency_mixed_iso: 'Amounts in different currencies cannot be combined.',
  currency_division_by_zero: 'An amount cannot be divided by zero.',
  currency_sub_cent_precision:
    'Amount must not include more than 2 decimal places.',
  currency_missing_iso: 'A currency code is required.',
  currency_invalid_payload: 'The currency value is malformed.',
  currency_invalid_amount: 'Amount must be a valid number.',
  currency_zero_decimal_precision:
    'This currency does not support fractional amounts.',
  currency_not_supported: 'This currency is not supported.',
  project_mixed_currency:
    'All amounts on a project must use the same currency.',
  invoice_mixed_currency:
    'All amounts on an invoice must use the same currency.',
  asset_transaction_currency_mismatch:
    "Sale and scrap prices must use the asset's currency.",
  currency_negative_amount: 'Amount cannot be negative.',
  hubspot_not_configured:
    'HubSpot is not connected for your organization. Connect HubSpot before configuring pipelines.',
  hubspot_invalid_pipeline:
    'That pipeline no longer exists in HubSpot. Refresh to load the current pipelines.',
  hubspot_invalid_pipeline_stage:
    'That stage no longer exists in HubSpot. Refresh to load the current stages.',
  customer_invalid_billing_contact_email:
    'Billing contact email is not a valid address. Correct it before saving.',
};

// Human-readable messages for ViolationMap errors. Keyed by the
// numeric statusCode the backend returns in
// metadata.errors[0].statusCode for unique/range/exclusion
// constraints. Codes are defined in
// project/asset-manager/src/constants/errors.go in the 5000-5100
// range.
//
// The {value} placeholder is replaced with entityValue from the
// response by getHumanFormattedError. Edit the strings in this map
// to change the wording shown to users.
/**
 * A move conflict names the first conflicting serial. Callers that want to say which
 * assets are affected read `conflictingAssets` off the error.
 */
export const ASSET_MOVE_CONFLICT_CODE = 1153;

export const VIOLATION_ERROR_MESSAGES: Record<number, string> = {
  5000: 'Another customer named {value} already exists.',
  5001: 'Another asset with serial number {value} already exists.',
  5002: 'Another location named {value} already exists for this customer.',
  5003: 'Another project named {value} already exists.',
  5004: 'This asset is already assigned to another project for an overlapping date range.',
  5005: 'A zone named {value} already exists at this location.',
  5006: 'Another item with SKU {value} already exists.',
  5007: 'Invoice number {value} is already in use.',
  5008: 'Another item named {value} already exists.',
  5009: 'Credit memo number {value} is already in use.',
  5010: 'Exchange number {value} is already in use.',
  5011: 'Fulfillment order number {value} is already in use.',
  5012: 'Purchase order number {value} is already in use.',
  5013: 'Return order number {value} is already in use.',
  5014: 'An asset event type named {value} already exists.',
  5015: 'A project workflow named {value} already exists.',
  5016: 'A payment term labeled {value} already exists.',
  5017: 'Serial {value} is already tracked for this item.',
  5018: 'An external user with email {value} already exists.',
  5019: 'An external user labeled {value} already exists.',
  5020: 'An item field named {value} already exists.',
  5021: 'This charge is already attached to an invoice.',
  5022: 'This shipment already has a shipping cost.',
};
